import { IMAGES } from "../game/assets";
import { ACTIONS, type Action, type KeyBindings } from "../game/input/bindings";
import { POWER_UP_ART } from "../game/render/power-up-art";
import { POWER_UP_KINDS } from "../sim";
import { el } from "./dom";
import { keyLabel } from "./key-labels";
import { POWER_UP_LABELS } from "./rule-options";

export const ACTION_LABELS: Record<Action, string> = {
	thrust: "Thrust",
	left: "Turn left",
	right: "Turn right",
	fireLaser: "Fire laser",
	fireMissile: "Fire missile",
};

/** Keys used outside ship control, shown in a neutral colour. */
export const SYSTEM_KEYS: ReadonlyArray<[code: string, action: string]> = [
	["Escape", "Pause / back"],
	["KeyF", "Fullscreen"],
	["KeyM", "Mute"],
];

const PLAYERS = [
	{ className: "p-blue", name: "Blue (player 1)" },
	{ className: "p-red", name: "Red (player 2)" },
	{ className: "p-green", name: "Green (player 3)" },
	{ className: "p-yellow", name: "Yellow (player 4)" },
];

/** `[code, width]`; an empty code is an invisible spacer. */
type KeyDef = [code: string, width?: number];

/** A compact ANSI keyboard: main block plus the arrow cluster (like Controls.JPG). */
const ROWS: KeyDef[][] = [
	[["Escape"], ["", 14]],
	[
		["Backquote"],
		...["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"].map((d): KeyDef => [`Digit${d}`]),
		["Minus"],
		["Equal"],
		["Backspace", 2],
	],
	[
		["Tab", 1.5],
		..."QWERTYUIOP".split("").map((c): KeyDef => [`Key${c}`]),
		["BracketLeft"],
		["BracketRight"],
		["Backslash", 1.5],
	],
	[
		["CapsLock", 1.75],
		..."ASDFGHJKL".split("").map((c): KeyDef => [`Key${c}`]),
		["Semicolon"],
		["Quote"],
		["Enter", 2.25],
	],
	[
		["ShiftLeft", 2.25],
		..."ZXCVBNM".split("").map((c): KeyDef => [`Key${c}`]),
		["Comma"],
		["Period"],
		["Slash"],
		["ShiftRight", 2.75],
	],
	[
		["ControlLeft", 1.25],
		["MetaLeft", 1.25],
		["AltLeft", 1.25],
		["Space", 6.25],
		["AltRight", 1.25],
		["MetaRight", 1.25],
		["ContextMenu", 1.25],
		["ControlRight", 1.25],
	],
];

const ARROWS: KeyDef[][] = [
	[[""], ["ArrowUp"], [""]],
	[["ArrowLeft"], ["ArrowDown"], ["ArrowRight"]],
];

const SHORT: Record<string, string> = {
	ShiftLeft: "Shift",
	ShiftRight: "Shift",
	ControlLeft: "Ctrl",
	ControlRight: "Ctrl",
	AltLeft: "Alt",
	AltRight: "Alt Gr",
	MetaLeft: "Win",
	MetaRight: "Win",
};

/** Which player (or system function) each key code is bound to. */
export function keyRoles(bindings: readonly KeyBindings[]): Map<string, string[]> {
	const roles = new Map<string, string[]>();
	const add = (code: string, role: string) => roles.set(code, [...(roles.get(code) ?? []), role]);
	bindings.forEach((b, i) => {
		for (const action of ACTIONS) {
			for (const code of b[action])
				add(code, `${PLAYERS[i]?.name ?? `Player ${i + 1}`}: ${ACTION_LABELS[action]}`);
		}
	});
	for (const [code, action] of SYSTEM_KEYS) add(code, action);
	return roles;
}

function keyboardDiagram(bindings: readonly KeyBindings[]): HTMLElement {
	const owner = new Map<string, string>();
	bindings.forEach((b, i) => {
		for (const action of ACTIONS)
			for (const code of b[action]) owner.set(code, PLAYERS[i]?.className ?? "");
	});
	for (const [code] of SYSTEM_KEYS) if (!owner.has(code)) owner.set(code, "p-system");
	const roles = keyRoles(bindings);

	const key = ([code, width = 1]: KeyDef) => {
		if (code === "") {
			const gap = el("span", { class: "kb-gap" });
			gap.style.flexGrow = String(width);
			return gap;
		}
		const cls = owner.get(code);
		const node = el("span", { class: `kb-key ${cls ?? ""}`.trim() }, SHORT[code] ?? keyLabel(code));
		node.style.flexGrow = String(width);
		const role = roles.get(code);
		if (role) node.title = role.join(", ");
		return node;
	};
	const rows = (defs: KeyDef[][]) =>
		defs.map((row) => el("div", { class: "kb-row" }, ...row.map(key)));

	return el(
		"div",
		{
			class: "kb",
			role: "img",
			"aria-label": "Keyboard layout with each player's keys highlighted",
		},
		el("div", { class: "kb-main" }, ...rows(ROWS)),
		el("div", { class: "kb-arrows" }, ...rows(ARROWS)),
	);
}

function bindingTable(bindings: readonly KeyBindings[]): HTMLElement {
	const header = el(
		"tr",
		{},
		el("th", {}, "Action"),
		...bindings.map((_, i) =>
			el("th", { class: PLAYERS[i]?.className ?? "" }, PLAYERS[i]?.name ?? `Player ${i + 1}`),
		),
		el("th", {}, "Gamepad"),
	);
	const gamepad: Record<Action, string> = {
		thrust: "A / RT / stick up",
		left: "Stick / d-pad left",
		right: "Stick / d-pad right",
		fireLaser: "X / RB",
		fireMissile: "B / LB",
	};
	const rows = ACTIONS.map((action) =>
		el(
			"tr",
			{},
			el("th", { scope: "row" }, ACTION_LABELS[action]),
			...bindings.map((b) => el("td", {}, ...keys(b[action]))),
			el("td", { class: "dim" }, gamepad[action]),
		),
	);
	return el(
		"table",
		{ class: "stats-table bindings-table" },
		el("thead", {}, header),
		el("tbody", {}, ...rows),
	);
}

function keys(codes: string[]): Node[] {
	if (codes.length === 0) return [el("span", { class: "dim" }, "unbound")];
	return codes.map((c) => el("kbd", {}, keyLabel(c)));
}

/** Content of the Help & Controls page for the given (possibly rebound) keys. */
export function helpContent(bindings: readonly KeyBindings[]): HTMLElement {
	return el(
		"div",
		{ class: "help" },
		keyboardDiagram(bindings),
		bindingTable(bindings),
		el(
			"p",
			{ class: "page-text" },
			...SYSTEM_KEYS.flatMap(([code, action], i) => [
				i > 0 ? " · " : "",
				el("kbd", {}, keyLabel(code)),
				` ${action}`,
			]),
		),
		el(
			"div",
			{ class: "how-to" },
			el("h2", {}, "How to play"),
			el(
				"ul",
				{},
				el(
					"li",
					{},
					"Planets pull on everything that moves — use their gravity to swing around them, but don't touch them.",
				),
				el(
					"li",
					{},
					"The world wraps around: fly off one edge and you come back on the other. The minimap shows where everyone is.",
				),
				el(
					"li",
					{},
					"Lasers fire rapidly but fade quickly and ignore gravity; missiles are slower, fall towards planets and can be shot down.",
				),
				el("li", {}, "Asteroids drift in from the spawn points. Ramming one destroys your ship."),
				el("li", {}, "Each pilot has 10 ships. Lose your last one and the match is over."),
			),
		),
		el(
			"div",
			{ class: "how-to" },
			el("h2", {}, "Modern and custom rules"),
			el(
				"ul",
				{},
				el(
					"li",
					{},
					"Laser overheat: every shot heats your laser (the bar under your ship). Hit the limit and it locks until it has cooled right down, so fire in bursts.",
				),
				el(
					"li",
					{},
					"Missiles are counted per ship; the HUD shows how many you have left. A new ship gets a full magazine.",
				),
				el(
					"li",
					{},
					"Homing missiles steer towards the nearest enemy in front of them. Cross close in front of one and it overshoots and loses you; or hide behind a planet and let it crash.",
				),
				el(
					"li",
					{},
					"Power-up pods appear around the map (coloured dots on the minimap). Fly through one to use it at once; asteroids smash them.",
				),
			),
			el(
				"ul",
				{ class: "power-up-list" },
				...POWER_UP_KINDS.map((kind) =>
					el(
						"li",
						{},
						el("img", {
							src: `assets/${IMAGES[POWER_UP_ART[kind].texture]}`,
							alt: "",
							width: 32,
							height: 32,
						}),
						el("strong", {}, POWER_UP_LABELS[kind].label),
						" ",
						POWER_UP_LABELS[kind].description,
					),
				),
			),
			el("p", { class: "page-text" }, "Every value can be changed under Rules → Custom…"),
		),
		el(
			"div",
			{ class: "how-to" },
			el("h2", {}, "Game modes"),
			el(
				"ul",
				{},
				el(
					"li",
					{},
					"Duel: the original. Each pilot has 10 ships; the last one with ships left wins.",
				),
				el(
					"li",
					{},
					"King of the Hill: hold the ring around the biggest planet on your own for a point a second (both inside = nobody scores). First to 30. Unlimited ships, 3 s to respawn.",
				),
				el(
					"li",
					{},
					"Capture the Flag: fly over the enemy flag to take it and bring it to your base (the ring around your flag) while your own flag is home. Carriers fly a little slower; a destroyed carrier drops the flag, which drifts with gravity until its owner touches it. First to 3.",
				),
			),
		),
		el(
			"div",
			{ class: "how-to" },
			el("h2", {}, "Online"),
			el(
				"ul",
				{},
				el(
					"li",
					{},
					"Online → Create room, pick map and rules, then send your friend the room code or the invite link. They join with Online → Join room, or just open the link.",
				),
				el(
					"li",
					{},
					"You fly the blue ship if you created the room, red if you joined, with either key set or a gamepad. Your ship answers instantly; the other one may jump slightly when their inputs arrive late.",
				),
				el(
					"li",
					{},
					"Esc opens the menu but doesn't pause: the match goes on. If your connection drops, the game reconnects by itself for a few seconds.",
				),
			),
		),
		el("p", { class: "page-text dim" }, `Gravity v${__APP_VERSION__}`),
	);
}
